// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import HeaderClickable from "@electrowire/App/Components/Header/HeaderClickable";
import SalesRepComponent from "@electrowire/App/Components/SalesRep/SalesRep";
import { TippyPopupStyle } from "@electrowire/App/Components/Tippy/Style";
import TippyPopup from "@electrowire/App/Components/Tippy/TippyPopup";
import { CustomAppState } from "@electrowire/App/Store/Reducers";
import { xIsAuthenticated } from "@electrowire/App/Store/Selectors/GeneralSelectors";
import MessageLines from "@electrowire/XDL/Icons/MessageLines";
import { color } from "@gcg/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { StyledButton } from "@insite/mobius/Clickable";
import { IconWrapper } from "@insite/mobius/Icon";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { FC } from "react";
import { css } from "styled-components";

enum fields {
    overrideTitle = "overrideTitle",
    link = "link",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.overrideTitle]: string;
        [fields.link]: LinkFieldValue;
    };
}

type Props = OwnProps;

export interface HeaderSalesRepStyles {
    tippyStyles?: TippyPopupStyle;
}

export const headerSalesRepStyles: HeaderSalesRepStyles = {
    tippyStyles: {
        trigger: {
            css: css`
                & > ${StyledButton} > ${TypographyStyle}, & > ${StyledButton} > ${IconWrapper} {
                    transition: transform ease-in-out 250ms;
                }

                &[aria-expanded="true"] {
                    & > ${StyledButton} > ${IconWrapper} {
                        color: ${color("common.ui8")};
                    }
                    & > ${StyledButton} > ${TypographyStyle} {
                        color: ${color("common.ui8")};
                    }
                }
            `,
        },
    },
};

const styles = headerSalesRepStyles;

const HeaderSalesRep: FC<Props> = ({ fields }) => {
    const link = useGetLink(fields.link);
    const history = useHistory();
    const { isTabletScreen, isAuthenticated } = useAppSelector(state => ({
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
        isAuthenticated: xIsAuthenticated(state),
    }));

    const openSalesRepPage = () => {
        if (link.url) {
            history.push(link.url);
        }
    };

    const headerClickableElement = (
        <HeaderClickable
            onClick={openSalesRepPage}
            iconProps={{
                src: MessageLines,
            }}
            data-test-selector="header-my-sales"
            aria-label="MySales"
        >
            {fields.overrideTitle || translate("My Sales")}
        </HeaderClickable>
    );

    if (!isAuthenticated) {
        return null;
    }

    if (isTabletScreen) {
        return headerClickableElement;
    }

    return (
        <TippyPopup
            content={<SalesRepComponent showTitle={true} withBorders={true} smallImageSize={false} />}
            arrow={true}
            offset={[0, 14]}
            maxWidth={400}
            placement="bottom-end"
            showCloseButton={false}
            extendedStyles={styles.tippyStyles}
        >
            {headerClickableElement}
        </TippyPopup>
    );
};

const widgetModule: WidgetModule = {
    component: HeaderSalesRep,
    definition: {
        displayName: "Header Sales Rep",
        icon: "arrow-right-to-bracket",
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.overrideTitle,
                editorTemplate: "TextField",
                defaultValue: "My Sales",
                fieldType: "General",
            },
            {
                name: fields.link,
                displayName: "Link",
                editorTemplate: "LinkField",
                defaultValue: { value: "", type: "Page" },
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
